import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { buildSeedPortfolios, uid } from "../mock";
import {
  apiListPortfolios,
  apiCreatePortfolio,
  apiUpdatePortfolio,
  apiDeletePortfolio,
} from "../api";

const PortfolioContext = createContext(null);

// Convert any absolute ".../api/files/<id>" media URL into a root-relative
// "/api/files/<id>" so media always resolves against the current environment
// (fixes broken images/videos when viewing in a different domain).
export const fixMedia = (val) => {
  if (typeof val === "string") {
    const i = val.indexOf("/api/files/");
    if (i > 0 && /^https?:\/\//i.test(val)) return val.slice(i);
    return val;
  }
  if (Array.isArray(val)) return val.map(fixMedia);
  if (val && typeof val === "object") {
    const out = {};
    for (const k of Object.keys(val)) out[k] = fixMedia(val[k]);
    return out;
  }
  return val;
};

const slugify = (str) =>
  str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export const PortfolioProvider = ({ children }) => {
  const [portfolios, setPortfolios] = useState([]);
  const [ready, setReady] = useState(false);
  const saveTimers = useRef({});
  const dataRef = useRef([]);

  useEffect(() => {
    dataRef.current = portfolios;
  }, [portfolios]);

  useEffect(() => {
    let mounted = true;
    let timer;
    const load = async () => {
      try {
        let list = await apiListPortfolios();
        if (!list || list.length === 0) {
          const seed = buildSeedPortfolios();
          await Promise.all(seed.map((p) => apiCreatePortfolio(p)));
          list = seed;
        }
        if (mounted) {
          setPortfolios((list || []).map(fixMedia));
          setReady(true);
        }
      } catch (e) {
        // Sin conexión: reintentar en vez de mostrar "no existe" (404 falso).
        if (mounted) timer = setTimeout(load, 3000);
      }
    };
    // En un portafolio público no estorbamos su carga: la lista se pide después.
    const path = window.location.pathname;
    const isPanel =
      path === "/" || /^\/(editor|cotizacion|cotizaciones|muestras|admin)(\/|$)/.test(path);
    if (isPanel) load();
    else timer = setTimeout(load, 4000);
    return () => {
      mounted = false;
      if (timer) clearTimeout(timer);
    };
  }, []);

  const getPortfolio = (id) => portfolios.find((p) => p.id === id || p.slug === id);

  const saveWithRetry = (id, portfolio, attempt = 0) => {
    apiUpdatePortfolio(id, portfolio).catch(() => {
      if (attempt < 6) setTimeout(() => saveWithRetry(id, portfolio, attempt + 1), 2500);
      else console.error("save error (agotado)");
    });
  };

  const scheduleSave = (portfolio) => {
    const id = portfolio.id;
    if (saveTimers.current[id]) clearTimeout(saveTimers.current[id]);
    saveTimers.current[id] = setTimeout(() => saveWithRetry(id, portfolio), 500);
  };

  // Guarda de inmediato lo último (al cerrar el editor / salir), con reintentos.
  const flushNow = (id) => {
    const p = dataRef.current.find((x) => x.id === id);
    if (saveTimers.current[id]) {
      clearTimeout(saveTimers.current[id]);
      saveTimers.current[id] = null;
    }
    if (p) saveWithRetry(id, p);
  };

  const updatePortfolio = (id, updater) => {
    setPortfolios((prev) => {
      const next = prev.map((p) =>
        p.id === id ? (typeof updater === "function" ? updater(p) : { ...p, ...updater }) : p
      );
      const updated = next.find((p) => p.id === id);
      if (updated) scheduleSave(updated);
      return next;
    });
  };

  const createPortfolio = (name = "Nuevo portafolio", kind = "portfolio") => {
    const id = uid();
    const isCard = kind === "card";
    const item = {
      id,
      kind,
      name,
      slug: `${slugify(name)}-${id.slice(-4)}`,
      domain: "uaicard",
      status: "active",
      startDate: new Date().toISOString().slice(0, 10),
      theme: { primary: "#2E6BFF", secondary: "#3B4FE0", background: "aurora" },
      background: { type: "aurora", url: "" },
      cover: isCard ? { type: "image", url: "", position: 50 } : undefined,
      profile: {
        avatar: "",
        avatarType: "image",
        avatarSize: 112,
        badgeStyle: "check",
        businessName: name,
        subtitle: "Descripción de tu negocio aquí",
        verified: false,
      },
      blocks: isCard
        ? [
            {
              id: uid(),
              type: "button",
              variant: "whatsapp",
              label: "WHATSAPP",
              value: "5210000000000",
              message: "Hola, quiero más información.",
            },
            { id: uid(), type: "button", variant: "email", label: "CORREO", value: "correo@ejemplo.com" },
            {
              id: uid(),
              type: "socials",
              items: [
                { id: uid(), platform: "instagram", url: "https://instagram.com" },
                { id: uid(), platform: "facebook", url: "https://facebook.com" },
              ],
            },
            { id: uid(), type: "mosaic", title: "GALERÍA", items: [] },
          ]
        : [
            { id: uid(), type: "heading", text: "¡CONTÁCTANOS!" },
            {
              id: uid(),
              type: "button",
              variant: "whatsapp",
              label: "ESCRÍBENOS",
              value: "5210000000000",
              message: "Hola, quiero más información.",
            },
          ],
    };
    setPortfolios((prev) => [item, ...prev]);
    apiCreatePortfolio(item).catch((e) => console.error("create error", e));
    return item;
  };

  const duplicatePortfolio = (id) => {
    const src = portfolios.find((p) => p.id === id);
    if (!src) return null;
    const newId = uid();
    const copy = {
      ...JSON.parse(JSON.stringify(src)),
      id: newId,
      name: `${src.name} (copia)`,
      slug: `${src.slug}-copia-${newId.slice(-4)}`,
      status: "active",
      startDate: new Date().toISOString().slice(0, 10),
    };
    delete copy._created;
    setPortfolios((prev) => [copy, ...prev]);
    apiCreatePortfolio(copy).catch((e) => console.error("dup error", e));
    return copy;
  };

  const renamePortfolio = (id, name) => {
    updatePortfolio(id, (p) => ({
      ...p,
      name,
      profile: { ...p.profile, businessName: name },
    }));
  };

  const toggleStatus = (id) => {
    updatePortfolio(id, (p) => ({
      ...p,
      status: p.status === "active" ? "suspended" : "active",
    }));
  };

  const deletePortfolio = (id) => {
    setPortfolios((prev) => prev.filter((p) => p.id !== id));
    apiDeletePortfolio(id).catch((e) => console.error("delete error", e));
  };

  return (
    <PortfolioContext.Provider
      value={{
        portfolios,
        ready,
        getPortfolio,
        updatePortfolio,
        flushNow,
        createPortfolio,
        duplicatePortfolio,
        renamePortfolio,
        toggleStatus,
        deletePortfolio,
      }}
    >
      {children}
    </PortfolioContext.Provider>
  );
};

export const usePortfolios = () => {
  const ctx = useContext(PortfolioContext);
  if (!ctx) throw new Error("usePortfolios must be used within PortfolioProvider");
  return ctx;
};
